<template>
  <div class="home" redesign>
    <!-- Header -->
    <div class="hero">
      <div class="inner-hero">
        <div class="frow inner-hero-wrapper">
          <div class="fcolumn inner-hero-left">
            <div class="inner-hero-left-content">
              <div class="gc-logo-wrapper">
                <img src="@/assets/images/grant-circle-logo.png" />
              </div>
              <div class="sub-heading">
                <h4 v-if="organizationDetails.homepageCopy" v-html="homeCopy"></h4>
                <h4
                  v-else-if="!organizationDetails.homepageCopy"
                  v-translate="{
                    progName: organizationDetails.programName,
                    orgName: organizationDetails.name
                  }"
                >
                  The %{progName} is a pool of funds for and funded by the
                  %{orgName} community to support each other during financial
                  emergencies.
                </h4>
              </div>

              <!-- Company logo -->
              <LogoHeader :org="organizationDetails" />
            </div>
          </div>

          <div class="hero-illustration inner-hero-right desktop">
            <HomeSvg :color="organizationDetails.darkBrandColor" />
          </div>
        </div>
      </div>
    </div>
    <div class="hero-illustration hidden-desktop">
      <HomeSvg :color="organizationDetails.darkBrandColor" />
    </div>

    <div class="cta-section-wrapper">
      <!-- Give Section -->
      <div class="cta-section">
        <h1 v-translate="{
          companyName: organizationDetails.name,
          programName: organizationDetails.programName
        }">
          Give to the %{companyName} %{programName}
        </h1>
        <h4 v-translate="{ companyName: organizationDetails.name }">
          Any donations to the fund will then be available to %{companyName} employees experiencing
          financial hardship.
        </h4>
        <button @click="onClickGive" class="form-button form-button-160">
          <span v-translate>Give</span>
        </button>
      </div>

      <!-- Request Section -->
      <div class="cta-section">
        <h1 v-translate>Request Grant Money</h1>
        <h4 v-translate="{ companyName: organizationDetails.name }">
          If you work for %{companyName} and an unexpected emergency has made it hard for you
          financially, request the money you need to get back on track.
        </h4>
        <button @click="onClickRequestGrantMoney" class="form-button form-button-160">
          <span v-translate>Request</span>
        </button>
      </div>
    </div>

    <div class="erf-help card-shape">
      <h1 v-translate="{ companyName: organizationDetails.name }">
        What if you don’t work for %{companyName}?
      </h1>
      <ul>
        <li>
          <h4>
            <a href="http://www.workwithcanary.com/partners" target="_blank" v-translate
              >Check here</a
            >&nbsp;<translate>to see if your organization is a %{organizationDetails.programName} partner.</translate>
          </h4>
        </li>
        <li>
          <h4>
            <span v-translate="{ progName: organizationDetails.programName }">
              Don’t see your organization listed but think %{progName} would be a great resource
              for your team?
            </span>
            >&nbsp;<a
              href="https://workwithcanary.com/refer-your-organization/"
              target="_blank"
              v-translate
              >Use this form</a
            >&nbsp;<translate>to connect us with your HR department.</translate>
          </h4>
        </li>
      </ul>
    </div>

    <!-- Articles Section -->
    <!-- <LoadSpinner v-if="!requestStory" /> -->
    <div v-if="requestStory" class="articles">
      <ArticleCard
        :header="articleCard.requestStory.header"
        :subHeader="articleCard.requestStory.subHeader"
        :article="requestStory"
        :buttonText="articleCard.requestStory.buttonText"
        @click="onClickRequestGrantMoney"
      />
    </div>

    <!-- NIP 9/30/20  Avoid having two stacked loading spinners by only rendering the second one if the first is gone -->
    <!-- <LoadSpinner v-if="!contributionStory && requestStory" /> -->
    <div v-if="contributionStory && organizationDetails.donationsEnabled" class="articles-light">
      <ArticleCard
        :header="articleCard.contributionStory.header"
        :subHeader="articleCard.contributionStory.subHeader"
        :article="contributionStory"
        :buttonText="articleCard.contributionStory.buttonText"
        @click="onClickGive"
        light
      />
    </div>

    <div v-if="organizationDetails.springFourEnabled" class="articles">
      <ArticleCard
        :header="articleCard.resources.header"
        :subHeader="articleCard.resources.subHeader"
        :buttonText="articleCard.resources.buttonText"
        @click="onClickFindResources"
      />
    </div>
    <!-- End Articles Section -->
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import ImpactStory from '@/services/impactStories'
import ArticleCard from '@/components/ArticleCard'
import LogoHeader from '@/components/LogoHeader'
import HomeSvg from '@/views/svg/CommunitySvg'
import { getDefaultForCustomVariable } from '@/services/organizationCustomVariables'
import { analyticsMixin } from '@/services/googleAnalytics'
import { formatText, formatValue } from '@/services/utils'

export default {
  name: 'EmergencyAssistance',
  mixins: [analyticsMixin],
  components: {
    ArticleCard,
    LogoHeader,
    HomeSvg,
  },
  data() {
    return {
      requestStory: null,
      contributionStory: null,
      demographicsModal: {
        surveyName: 'Household Details',
        surveyResponse: null,
        questions: [96, 97], //Hardcoded demographic question HumanReadableIDs from CAN-570
        isOpen: false,
      },
      communicationsModal: false,
    }
  },
  async created() {
    // Load ImpactStories
    ImpactStory.api.getStories().then(stories => {
      // 0th index should always be REQUEST_STORY, 1st index should always be CONTRIBUTION_STORY
      this.requestStory = stories[0]
      this.contributionStory = stories[1]
    })
  },
  methods: {
    getDefaultForCustomVariable,
    navigateToSignUp() {
      if (this.organizationDetails.enableSaml) {
        const currentLocation = window.location.href
        window.location = `${process.env.VUE_APP_API_ENDPOINT}/saml2/login?from=${currentLocation}&RelayState=${currentLocation}`
        return
      }

      this.$router.push({ name: 'Qualification' })
    },
    onClickRequestGrantMoney() {
      if (this.userIsLoggedIn) {
        this.$router.push({ name: 'NewRequest' })
      } else {
        this.$router.push({ name: 'Qualification' })
      }
    },
    onClickGive() {
      this.$router.push({ name: 'AddPayment' })
    },
    onClickFindResources() {
      this.eventsTracker('clicked-cta-resources')
      this.$router.push({ name: 'FindResources' })
    },
  },
  computed: {
    giveSubHeaderCopy() {
      const translated = this.$gettext(
        "Add money to the pool so it's there when you or your %{orgDetails} need it.",
      )
      return this.$gettextInterpolate(translated, {
        orgDetails:
          (this.organizationDetails &&
            this.organizationDetails.customVariables &&
            this.$options.filters.translateToCurrentLang(
              this.$language.current,
              this.organizationDetails.customVariables.cv2,
              this.organizationDetails.customVariables.cv2,
            )) ||
          getDefaultForCustomVariable('cv2', this.$language.current),
      })
    },
    homeCopy() {
      const varMap = { org: this.organizationDetails }
      return formatValue(this.organizationDetails.homepageCopy, varMap)
    },
    articleCard() {
      const translated = this.$gettext(
        "Add money to the pool so it's there when you or your %{orgDetails} need it.",
      )

      return {
        requestStory: {
          header: this.$gettext('Request Grant Money'),
          subHeader: this.$gettext(
            'If an unexpected emergency has made it hard for you financially, request the money you need to get back on track.',
          ),
          buttonText: this.$gettext('Request'),
        },
        contributionStory: {
          header: this.$gettext('Give to the') + ' ' + this.organizationDetails.programName,
          subHeader: this.$gettextInterpolate(translated, {
            orgDetails:
              (this.organizationDetails &&
                this.organizationDetails.customVariables &&
                this.$options.filters.translateToCurrentLang(
                  this.$language.current,
                  this.organizationDetails.customVariables.cv2,
                  this.organizationDetails.customVariables.cv2,
                )) ||
              getDefaultForCustomVariable('cv2', this.$language.current),
          }),
          buttonText: this.$gettext('Give'),
        },
        resources: {
          header: this.$gettext('Find money-saving resources'),
          subHeader: this.$gettext(
            'Connect with local, trusted resources designed to help you lower monthly costs and achieve financial stability.',
          ),
          buttonText: this.$gettext('Find Resources'),
        },
      }
    },
    ...mapState(['organizationDetails', 'user']),
    ...mapGetters(['userIsLoggedIn']),
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/mixins/buttons';
@import '@/styles/mixins/containers';
@import '@/styles/flex-box';
@import '@/styles/mixins/responsive';

.home {
  display: flex;
  flex-direction: column;
}

.hero {
  background-color: var(--light-brand-color);
  border-top-left-radius: 80px;
  border-bottom-right-radius: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 1rem;

  @include tablet-screen {
    border-radius: 80px 0 80px 0;
    padding: 2rem;
  }

  @include desktop-screen {
    border-radius: 120px 0 120px 0;
    padding: 4rem 2rem;
  }

  .inner-hero {
    display: flex;
    flex-direction: row;
    align-items: center;
    background-color: #ffffff;
    border-radius: 64px 0 64px 0;
    padding: 60px 24px;
    z-index: 10;
    width: 100%;

    @include tablet-screen {
      border-radius: 48px 0;
      padding: 4rem;
    }

    @include desktop-screen {
      background-color: transparent;
      padding: 0;
    }

    .inner-hero-wrapper {
      justify-content: space-around;
      width: 100%;

      @include tablet-screen {
        justify-content: center;
      }
    }

    .inner-hero-left,
    .inner-hero-right {
      align-content: space-evenly;
      align-items: center;
      justify-content: center;

      @include desktop-screen {
        width: 50%;
      }
    }

    .inner-hero-left {
      @include mobile-screen {
        max-width: 500px;
      }

      @include tablet-screen {
        max-width: 500px;
      }

      @include desktop-screen {
        min-width: 350px;
        max-width: 450px;
        margin: 0 2rem;
        justify-content: center;
      }
    }

    .inner-hero-left-content {
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .inner-hero-right {
      overflow: hidden;
      border-radius: 80px 0;
    }
  }
}

.hero-illustration {
  z-index: 8;

  &.hidden-desktop {
    border-radius: 0 0 64px 0;
    position: relative;
    top: -120px;
    height: 310px;
    overflow: hidden;
    margin-bottom: -120px;

    @include tablet-screen {
      height: 475px;
      top: -280px;
      margin-bottom: -280px;
    }
  }

  &.desktop {
    svg {
      min-height: 100%;
    }
  }
}

.gc-logo-wrapper {
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;

  img {
    width: 70%;
  }
}

.sub-heading {
  font-size: 1.25rem;
  margin-bottom: 2rem;
  text-align: center;

  h4 {
    margin-bottom: 0;
  }

  ::v-deep p {
    font-size: 1.25rem;
    line-height: 1.75rem;
    margin-bottom: 0;
  }

  ::v-deep p:last-of-type {
    margin-bottom: 0;
  }
}

.articles {
  background-color: var(--light-brand-color);
  border-radius: 5rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin-bottom: 2.5rem;
  margin-top: 2.5rem;

  &-light {
    background-color: white;
  }

  @include tablet-and-up-screen {
    border-radius: 7.5rem 0;
  }
}

.cta-section-wrapper,
.erf-help {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  margin: 0rem 1rem 2rem 1rem;

  @include tablet-screen {
    flex-direction: row;
    margin: 2.5rem 2rem;

    .cta-section {
      width: 50%;
      align-self: stretch;
      margin-bottom: 2.5rem;

      &:nth-of-type(1) {
        margin-right: 1rem;
      }

      &:nth-of-type(2) {
        margin-left: 1rem;
      }
    }
  }

  @include desktop-screen {
    flex-direction: row;
    padding: 4rem 0;

    .cta-section {
      max-width: 420px;
      margin-top: 0;
      padding: 0;
      align-self: stretch;

      h1 {
        font-size: 2.25rem;
      }
    }
  }
}

.join-now,
.cta-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-top: 2.5rem;
  align-self: center;
  max-width: 90%;
  justify-content: space-between;

  h1 {
    font-size: 1.5rem;
    font-weight: bold;
    line-height: 2rem;
  }

  h4 {
    font-size: 1.25rem;
    line-height: 1.75rem;
    margin-bottom: 2rem;
  }

  h5 {
    margin-bottom: 0;

    span {
      font-weight: 500;
      font-size: 1rem;
      line-height: 1.5rem;
    }
  }
}

.erf-help {
  background-color: var(--light-brand-color);
  flex-direction: column;
  align-self: center;
  justify-self: center;
  padding: 2rem;
  max-width: 768px;

  h1 {
    font-size: 1.5rem;
    font-weight: bold;
    line-height: 2rem;
  }

  h4 {
    font-size: 1.25rem;
    line-height: 1.75rem;
    margin-bottom: 2rem;
  }

  li:last-of-type {
    h4 {
      margin-bottom: 0;
    }
  }

  @include desktop-screen {
    h1 {
      font-size: 2.25rem;
    }
  }
}

.logo-wrapper {
  height: 12rem;
  width: 100%;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
